import { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CreditCard, CheckCircle, Copy, UploadSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChargePay() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isSuccess = searchParams.get('success') === 'true';

  const [charge, setCharge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState('loading');
  const [checkoutUrl, setCheckoutUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const fetchCharge = async () => {
    try {
      const { data } = await axios.get(`${API}/charges/${id}`);
      setCharge(data);
      if (data.payment_status === 'pago') setStep('success');
      else setStep(isSuccess ? 'checking' : 'pay');
    } catch {
      setStep('notfound');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCharge(); /* eslint-disable-next-line */ }, [id]);

  // Poll payment confirmation after returning from Stripe
  useEffect(() => {
    const sid = searchParams.get('session_id');
    if (!isSuccess || !sid) return;
    let n = 0;
    const chk = async () => {
      n += 1;
      try {
        const { data } = await axios.get(`${API}/charges/${id}/checkout-status/${sid}`);
        if (data.payment_status === 'paid') { setStep('success'); return; }
      } catch { /* retry */ }
      if (n < 6) setTimeout(chk, 1500); else setStep('success');
    };
    chk();
    /* eslint-disable-next-line */
  }, [isSuccess]);

  const payCard = async () => {
    try {
      const { data } = await axios.post(`${API}/charges/${id}/checkout`, { origin_url: window.location.origin });
      if (data.url) {
        setCheckoutUrl(data.url);
        setStep('redirecting');
        setTimeout(() => { try { window.location.assign(data.url); } catch { /* manual button */ } }, 700);
      }
    } catch { toast.error('Erro ao iniciar pagamento'); }
  };

  const onZelleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/charges/${id}/zelle-receipt`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Comprovante enviado!');
      setStep('success');
    } catch { toast.error('Erro ao enviar comprovante'); }
    finally { setUploading(false); }
  };

  if (loading) {
    return <div className="min-h-screen bg-[#0B1120] flex items-center justify-center">
      <div className="w-12 h-12 border-4 border-[#E85D04] border-t-transparent rounded-full animate-spin" /></div>;
  }

  if (step === 'redirecting') {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-6 text-center">
        <div className="w-14 h-14 rounded-full border-4 border-[#E85D04] border-t-transparent animate-spin mb-6" />
        <h2 className="font-['Outfit'] text-xl font-bold text-[#1A1A1A]">Abrindo pagamento seguro...</h2>
        <p className="text-sm text-[#73706A] mt-2">Se a página não abrir, toque no botão abaixo.</p>
        {checkoutUrl && <a href={checkoutUrl} target="_self" rel="noopener" className="inline-flex items-center justify-center w-full max-w-sm bg-[#E85D04] hover:bg-[#DC2F02] text-white font-semibold py-4 rounded-lg text-lg mt-6" data-testid="charge-open-checkout">Ir para o pagamento</a>}
        {checkoutUrl && <button onClick={() => { navigator.clipboard?.writeText(checkoutUrl); toast.success('Link copiado'); }} className="mt-3 text-sm text-[#0077B6] underline">Copiar link do pagamento</button>}
        <p className="text-xs text-[#73706A] mt-4 max-w-sm">Está pelo Instagram/Facebook? Abra no Safari ou Chrome para pagar.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0B1120] via-[#0F172A] to-[#0B1120] flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden" data-testid="charge-pay-page">
        <div className="bg-[#1A1A1A] px-6 py-5 text-center">
          <p className="text-[#FFB703] text-xs uppercase tracking-widest font-semibold">Tia Karol Sports</p>
          <p className="text-white/80 text-sm mt-1">{charge?.client_name}</p>
        </div>

        {step === 'notfound' && (
          <div className="p-8 text-center text-[#73706A]">Cobrança não encontrada.</div>
        )}

        {step === 'success' && (
          <div className="p-8 text-center" data-testid="charge-success">
            <div className="w-16 h-16 rounded-full bg-[#2D6A4F] flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={40} weight="fill" className="text-white" />
            </div>
            <h2 className="font-['Outfit'] text-2xl font-bold text-[#1A1A1A]">Pagamento recebido!</h2>
            <p className="text-[#73706A] mt-2">Obrigado, {charge?.client_name}. Seu pagamento de ${charge?.amount?.toFixed(2)} foi registrado.</p>
          </div>
        )}

        {step === 'checking' && (
          <div className="p-8 text-center">
            <div className="w-12 h-12 border-4 border-[#E85D04] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-[#73706A]">Confirmando seu pagamento...</p>
          </div>
        )}

        {(step === 'pay' || step === 'zelle') && (
          <div className="p-6">
            <div className="text-center mb-6">
              <p className="text-[#73706A] text-sm">Valor a pagar</p>
              <p className="font-['Outfit'] text-5xl font-bold text-[#1A1A1A] mt-1" data-testid="charge-amount">${charge?.amount?.toFixed(2)}</p>
              {charge?.description && <p className="text-[#73706A] text-sm mt-2">{charge.description}</p>}
            </div>

            {step === 'pay' && (
              <div className="space-y-3">
                <button onClick={payCard} className="w-full flex items-center justify-center gap-2 bg-[#0077B6] hover:bg-[#03045E] text-white font-semibold py-4 rounded-xl transition-colors" data-testid="charge-pay-card">
                  <CreditCard size={20} /> Pagar com Cartão
                </button>
                <button onClick={() => setStep('zelle')} className="w-full flex items-center justify-center gap-2 bg-white border-2 border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white font-semibold py-4 rounded-xl transition-colors" data-testid="charge-pay-zelle">
                  Pagar com Zelle
                </button>
              </div>
            )}

            {step === 'zelle' && (
              <div className="space-y-4" data-testid="charge-zelle-box">
                <div className="bg-[#F5F2EB] rounded-xl p-4">
                  <p className="text-sm text-[#73706A]">Envie <strong className="text-[#1A1A1A]">${charge?.amount?.toFixed(2)}</strong> via Zelle para:</p>
                  <div className="flex items-center justify-between mt-2 bg-white rounded-lg px-3 py-2">
                    <span className="font-mono font-semibold text-[#1A1A1A]">{charge?.zelle_phone}</span>
                    <button onClick={() => { navigator.clipboard?.writeText(charge?.zelle_phone); toast.success('Copiado'); }} className="p-2 rounded-lg hover:bg-[#E6E2D8]"><Copy size={18} /></button>
                  </div>
                </div>
                <p className="text-sm text-[#73706A]">Depois de enviar, anexe o comprovante abaixo:</p>
                <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={onZelleFile} data-testid="charge-zelle-input" />
                <button onClick={() => fileRef.current?.click()} disabled={uploading} className="w-full flex items-center justify-center gap-2 bg-[#2D6A4F] hover:bg-[#1B4332] text-white font-semibold py-4 rounded-xl transition-colors disabled:opacity-50" data-testid="charge-zelle-upload">
                  <UploadSimple size={20} /> {uploading ? 'Enviando...' : 'Enviar comprovante Zelle'}
                </button>
                <button onClick={() => setStep('pay')} className="w-full text-sm text-[#73706A]">Voltar</button>
              </div>
            )}

            <p className="text-center text-xs text-[#9CA3AF] mt-6">🔒 Pagamento seguro</p>
          </div>
        )}
      </div>
    </div>
  );
}
